/**
 * Чек-лист гостевой заявки в номер / на ремонт (#79, гостевой дом).
 *
 * Один экран с компактными выбираемыми надписями (chip-toggle), полем
 * «Комментарий» и кнопкой «Отправить». На отправку создаётся ОДНА заявка
 * нужного отдела (housekeeping / maintenance) со списком выбранных пунктов и
 * комментарием в описании — без дробления на несколько заявок и без новой
 * схемы заявок (используем штатный POST /v1/requests, IDOR #329 — гость
 * создаёт заявку только себе).
 *
 * Паттерны (BINDING): отдельный экран с «Назад» сверху в брендовом стиле
 * (.ta-back-btn), Friendly-тон «Вы» с заглавной, мобайл 375. Комментарий —
 * обычный textarea (НЕ select, шеврон не вешаем — патент про стрелки).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import {
  ServiceCategoryId,
  type ServiceRequestCreatedResponse,
} from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useServiceCatalog, useCreateServiceRequest } from '../catalog/useCatalog.js';

export interface RoomRequestConfig {
  /** Заголовок экрана («В номер» / «Ремонт»). */
  title: string;
  /** Код catalog-категории отдела (housekeeping / maintenance). */
  categoryCode: string;
  /** Заголовок создаваемой заявки. */
  requestTitle: string;
  /** Пункты-чекбоксы. */
  options: readonly string[];
  /** testid-префикс. */
  testId: string;
}

/** Собирает описание заявки из выбранных пунктов и комментария. */
function buildDescription(selected: readonly string[], comment: string): string {
  const lines: string[] = [];
  if (selected.length > 0) {
    lines.push('Выбрано: ' + selected.join(', '));
  }
  const c = comment.trim();
  if (c !== '') {
    lines.push('Комментарий: ' + c);
  }
  return lines.join('\n');
}

function RoomRequestForm({
  config,
  categoryId,
}: {
  config: RoomRequestConfig;
  categoryId: string;
}): JSX.Element {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ServiceRequestCreatedResponse | null>(null);
  const mutation = useCreateServiceRequest();

  const toggle = (opt: string): void => {
    setSelected((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt],
    );
  };

  const canSubmit = selected.length > 0 || comment.trim() !== '';

  const onSubmit = (): void => {
    setError(null);
    const description = buildDescription(selected, comment);
    mutation.mutate(
      {
        category_id: ServiceCategoryId(categoryId),
        title: config.requestTitle,
        ...(description !== '' && { description }),
      },
      {
        onSuccess: (resp) => setDone(resp),
        onError: (err) =>
          setError(
            err.message !== ''
              ? err.message
              : 'Не получилось отправить — попробуйте ещё раз.',
          ),
      },
    );
  };

  if (done) {
    return (
      <section data-testid={`${config.testId}-success`}>
        <Toast message="Заявка отправлена · принято в работу" />
        <Card>
          <h2 className="hsc-card__title">Готово!</h2>
          <p>Мы передали Вашу заявку отелю — следить за статусом можно в разделе «Мои заявки».</p>
          <div className="rq-actions">
            <Button
              data-testid={`${config.testId}-open`}
              onClick={() => navigate(`/requests/${encodeURIComponent(done.id)}`)}
            >
              Открыть заявку
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              На главную
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid={`${config.testId}-page`}>
      <button type="button" className="ta-back-btn" onClick={() => navigate('/')} data-testid={`${config.testId}-back`}>
        <span className="ta-back-btn__chev" aria-hidden="true">
          ‹
        </span>{' '}
        Назад
      </button>
      <h1 className="hsc-page__title">{config.title}</h1>
      <p className="ta-muted">Отметьте, что нужно — мы всё принесём / починим.</p>

      {error !== null && (
        <div className="ta-error-summary" role="alert" data-testid={`${config.testId}-error`}>
          <strong>Не получилось отправить:</strong> {error}
        </div>
      )}

      <div className="ta-checkchips" role="group" aria-label={config.title} data-testid={`${config.testId}-options`}>
        {config.options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              role="checkbox"
              aria-checked={active}
              className={active ? 'ta-checkchip ta-checkchip--on' : 'ta-checkchip'}
              data-testid={`${config.testId}-opt-${opt}`}
              onClick={() => toggle(opt)}
            >
              <span className="ta-checkchip__box" aria-hidden="true">
                {active ? '✓' : ''}
              </span>
              <span className="ta-checkchip__label">{opt}</span>
            </button>
          );
        })}
      </div>

      <div className="ta-field" style={{ marginTop: 16 }}>
        <label className="hsc-input__label" htmlFor={`${config.testId}-comment`}>
          Комментарий
        </label>
        <textarea
          id={`${config.testId}-comment`}
          className="ta-textarea"
          rows={3}
          placeholder="Если хотите что-то добавить или не нашлось нужного пункта"
          data-testid={`${config.testId}-comment`}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
      </div>

      <div className="rq-actions">
        <Button
          type="button"
          loading={mutation.isPending}
          disabled={canSubmit ? undefined : true}
          onClick={onSubmit}
          data-testid={`${config.testId}-submit`}
        >
          Отправить
        </Button>
      </div>
    </section>
  );
}

export function RoomServiceRequestPage({ config }: { config: RoomRequestConfig }): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useServiceCatalog(locale);

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid={`${config.testId}-skeleton`}>
        <Skeleton height={40} radius={12} />
        <div style={{ height: 12 }} />
        <Skeleton height={160} radius={14} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section>
        <h1 className="hsc-page__title">{config.title}</h1>
        <Card>
          <p role="alert">Сейчас не получается открыть форму. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              На главную
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  const category = data.categories.find((c) => c.code === config.categoryCode);

  if (!category) {
    // Категория отдела не заведена в каталоге — деградируем к свободной заявке
    // первой доступной категории, не теряя данные гостя.
    const fallback = data.categories[0];
    if (!fallback) {
      return (
        <section data-testid={`${config.testId}-no-category`}>
          <button type="button" className="ta-back-btn" onClick={() => navigate('/')}>
            <span className="ta-back-btn__chev" aria-hidden="true">
              ‹
            </span>{' '}
            Назад
          </button>
          <h1 className="hsc-page__title">{config.title}</h1>
          <Card>
            <p>Каталог отеля ещё наполняется — попробуйте позже или обратитесь на ресепшен.</p>
          </Card>
        </section>
      );
    }
    return <RoomRequestForm config={config} categoryId={fallback.id} />;
  }

  return <RoomRequestForm config={config} categoryId={category.id} />;
}

/* Конфиги двух экранов (#79, дословный список пунктов Артёма). */

export const IN_ROOM_CONFIG: RoomRequestConfig = {
  title: 'В номер',
  categoryCode: 'housekeeping',
  requestTitle: 'Заявка в номер',
  testId: 'in-room',
  options: [
    'вода',
    'полотенца',
    'тапочки',
    'халат',
    'постельное бельё',
    'посуда',
    'чай/кофе',
    'шампунь',
    'гель для душа',
    'зубная щётка',
    'туалетная бумага',
  ],
};

export const REPAIR_CONFIG: RoomRequestConfig = {
  title: 'Ремонт',
  categoryCode: 'maintenance',
  requestTitle: 'Заявка на ремонт',
  testId: 'repair',
  options: [
    'телевизор',
    'пульт',
    'кровать',
    'занавески',
    'туалет',
    'душ',
    'раковина',
    'чайник',
    'холодильник',
  ],
};
